<template>
  <v-container class="py-8 py-md-10 py-lg-12">
    <TerrainSearchBasicForm v-if="!xsOnly" />
    <DialogTerrainSearchBasicWidget v-else />

    <LoadingWidget v-if="loading" />

    <template v-else-if="items.length">
      <div
        v-if="hasFilter"
        class="
          text-body-2 text-sm-body-1 text-center
          font-weight-regular
          px-3
          mt-3 mt-md-5
        "
      >
        Sua busca retornou
        <span class="font-weight-bold">{{ totalItems }}</span> resultado(s)
      </div>

      <v-sheet
        class="d-flex justify-end mx-auto mt-3 search-basic-form"
        max-width="1180px"
      >
        <div>
          <OrderSelect v-on:reorder="getList()" />
        </div>
      </v-sheet>

      <v-data-table
        ref="terrainsTable"
        class="mt-3 terrains-table"
        :headers="headers"
        :items="parsedItems"
        :items-per-page="-1"
        @click:row="expandRow"
        no-data-text="Nenhum terreno encontrado"
        locale="pt-BR"
        hide-default-footer
        disable-sort
        show-expand
        single-expand
        :expanded.sync="expanded"
      >
        <template #expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <LandCard v-bind="item" />
          </td>
        </template>
        <template #item.share-link="{ item }">
          <TooltipWidget tip="Abrir em nova aba" top>
            <v-btn
              @click.stop="openLink(item.to)"
              rel="noopener"
              color="primary"
              icon
              small
            >
              <v-icon small>fas fa-external-link-alt</v-icon>
            </v-btn>
          </TooltipWidget>
        </template>
        <template #item.data-table-expand>
          <TooltipWidget tip="Expandir informações" top>
            <v-btn rel="noopener" color="primary" icon small>
              <v-icon small>{{ dataTableExpandIcon }}</v-icon>
            </v-btn>
          </TooltipWidget>
        </template>
      </v-data-table>

      <PaginationWidget
        v-if="countPages > 1"
        class="mt-8 mt-md-10"
        :count="countPages"
      />
    </template>

    <v-alert v-else class="mt-8 mt-md-10 mt-lg-12" type="info">
      Nenhum imóvel encontrado
    </v-alert>
  </v-container>
</template>

<script>
import LoadingWidget from "../components/widgets/LoadingWidget.vue";
import TerrainSearchBasicForm from "../components/forms/TerrainSearchBasicForm.vue";
import DialogTerrainSearchBasicWidget from "../components/widgets/DialogTerrainSearchBasicWidget.vue";
import OrderSelect from "../components/forms/fields/OrderSelect.vue";
import TooltipWidget from "../components/widgets/TooltipWidget.vue";
import LandCard from "../components/cards/LandCard.vue";
import PaginationWidget from "../components/widgets/PaginationWidget.vue";
import { formatArea } from "../utils/formatArea";
import { formatPrice } from "../utils/formatPrice";
import { formatToURL } from "../utils/formatToURL";
import { getCookie } from "../utils/cookie";

const _config = {
  mod: "properties",
};

const _pagination = {
  itemsPerPage: 50,
  returnTotalItems: 1,
};

const _data = {
  loading: true,
  headers: [
    { text: "Referência", value: "reference" },
    { text: "Bairro", value: "district" },
    { text: "Tamanho", value: "area" },
    { text: "Valor Total", value: "price" },
    { text: "Valor por m²", value: "price_per_area" },
    { text: "", value: "share-link", align: "right" },
    { text: "", value: "data-table-expand" },
  ],
  totalItems: null,
  items: [],
  expanded: [],
};

export default {
  data: () => ({
    ..._data,
  }),
  computed: {
    query() {
      return this.$route.query;
    },
    hasFilter() {
      if (
        this.category !== "Terreno,Terreno Comercial" ||
        this.district ||
        this.city ||
        this.reference ||
        this.totalArea ||
        this.price
      )
        return true;

      return false;
    },
    page() {
      return this.query.p;
    },
    category() {
      return this.query.tipo ? this.query.tipo : "Terreno,Terreno Comercial";
    },
    price() {
      return this.query.preco ? this.query.preco.split(",") : null;
    },
    city() {
      return this.query.cidade;
    },
    district() {
      return this.query.bairro;
    },
    reference() {
      return this.query.referencia;
    },
    condominium() {
      return this.query.condominio;
    },
    totalArea() {
      return this.query["area-total"]
        ? this.query["area-total"].split(",")
        : null;
    },
    countPages() {
      return Math.ceil(this.totalItems / _pagination.itemsPerPage);
    },
    districts() {
      return this.$rest("districts").list;
    },
    parsedItems() {
      return this.mapLands(this.items);
    },
    dataTableExpandIcon() {
      return this.expanded.length ? "fas fa-chevron-up" : "fas fa-chevron-down";
    },
    xsOnly() {
      return this.$vuetify.breakpoint.xsOnly;
    },
  },
  methods: {
    getList() {
      this.loading = true;

      this.items = [];

      const params = { ..._pagination };

      if (this.page) params.page = this.page;
      if (this.category) params.category = this.category;
      if (this.city) params.city = this.city;
      if (this.district) params.district = this.district;
      if (this.reference) params.reference = this.reference;
      if (this.condominium) params.condominium = this.condominium;
      if (this.totalArea) params.landArea = this.totalArea;

      params.order = this.getCookie("ordenar");

      if (this.price) {
        params.minPrice = this.price[0];
        params.maxPrice = this.price[1];
      }

      this.$rest(_config.mod)
        .get({
          params,
          save: (state, data) => {
            if (data?.list?.length) {
              this.items = data.list;
              this.totalItems = data.totalItems;
            } else this.items = data;
          },
        })
        .finally(() => (this.loading = false));
    },
    mapLands(lands) {
      return lands.map((land) => {
        const mappedLand = { ...land };

        mappedLand._mapped = true;

        mappedLand.price_per_area = "--";

        if (mappedLand.price > 0 && mappedLand.total_area > 0)
          mappedLand.price_per_area = formatPrice(
            mappedLand.price / mappedLand.total_area
          );

        mappedLand.area = "--";

        if (mappedLand.total_area > 0)
          mappedLand.area = formatArea(mappedLand.total_area);

        mappedLand.parsedPrice = "--";

        if (mappedLand.price > 0)
          mappedLand.parsedPrice = formatPrice(mappedLand.price);

        mappedLand.price = mappedLand.parsedPrice;

        mappedLand.text = mappedLand.text
          ? mappedLand.text.replace(
              /([^>\r\n]?)(\r\n|\n\r|\r|\n)/g,
              "$1" + "<br />" + "$2"
            )
          : "";

        mappedLand.to = `/terrenos/${formatToURL(this.title(mappedLand))}-${
          mappedLand.reference
        }`;

        return mappedLand;
      });
    },
    title(item) {
      if (!item.category || !item.district) return null;

      const category = item.category.split(" ").pop();
      const district = item.district.split(" ");
      let parsedDistrict = item.district;

      if (category == district[0])
        parsedDistrict = parsedDistrict.replace(category, "");

      return item.category + " " + parsedDistrict;
    },
    expandRow(item) {
      if (this.expanded[0] && this.expanded[0].id === item.id) {
        this.expanded = [];
      } else {
        this.expanded = [item];
      }
    },
    openLink(link) {
      window.open(link, "_blank");
    },
    getCookie,
  },
  watch: {
    $route() {
      this.getList();
    },
  },
  created() {
    this.getList();
  },
  components: {
    LoadingWidget,
    TerrainSearchBasicForm,
    DialogTerrainSearchBasicWidget,
    OrderSelect,
    TooltipWidget,
    LandCard,
    PaginationWidget,
  },
};
</script>

<style lang="scss">
.terrains-table {
  border: 1px solid #ccc;

  color: var(--v-primary-base) !important;

  th,
  td {
    color: var(--v-primary-base) !important;
  }

  tr:not(.v-data-table__expanded__content) {
    cursor: pointer;
  }

  .v-data-table__expanded__row {
    background-color: #f5f5f5;
  }

  tr > :nth-last-child(1),
  tr > :nth-last-child(2) {
    padding: 0px 2px !important;
  }

  .v-data-table__expanded__content {
    box-shadow: none !important;

    > td {
      padding: 0 !important;
    }
  }
}

@media screen and (max-width: 600px) {
  .terrains-table {
    .v-data-table__mobile-table-row {
      padding-top: 12px;

      .v-data-table__mobile-row {
        min-height: 32px !important;
      }
    }

    .v-data-table__expanded__content > td {
      display: block;
      height: 100% !important;
    }
  }
}
</style>
